import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface Measurement {
    id: string;
    timestamp: string;
    weight?: number;
    height?: number;
    bodyFatPercentage?: number;
    muscleMass?: number;
    waistCircumference?: number;
    hipCircumference?: number;
    [key: string]: any;
}

export const exportMeasurementsToPDF = (
    measurements: Measurement[],
    patientName: string,
    filters?: {
        startDate?: string;
        endDate?: string;
        includeGraphs?: boolean;
    }
) => {
    const doc = new jsPDF();

    // Title
    doc.setFontSize(18);
    doc.setTextColor(15, 23, 42); // #0f172a
    doc.text('Relatório de Medições Antropométricas', 14, 20);

    // Subtitle
    doc.setFontSize(12);
    doc.setTextColor(100, 116, 139); // #64748b
    doc.text(`Paciente: ${patientName}`, 14, 28);
    doc.text(`Data de Emissão: ${new Date().toLocaleDateString('pt-BR')}`, 14, 34);

    if (filters?.startDate || filters?.endDate) {
        const periodText = `Período: ${filters.startDate ? new Date(filters.startDate).toLocaleDateString('pt-BR') : 'Início'} até ${filters.endDate ? new Date(filters.endDate).toLocaleDateString('pt-BR') : 'Hoje'}`;
        doc.text(periodText, 14, 40);
    }

    // Line separator
    doc.setDrawColor(226, 232, 240); // #e2e8f0
    doc.line(14, 45, 196, 45);

    // Filter measurements by date if specified
    let filteredMeasurements = [...measurements];
    if (filters?.startDate) {
        filteredMeasurements = filteredMeasurements.filter(m =>
            new Date(m.timestamp) >= new Date(filters.startDate!)
        );
    }
    if (filters?.endDate) {
        filteredMeasurements = filteredMeasurements.filter(m =>
            new Date(m.timestamp) <= new Date(filters.endDate!)
        );
    }

    // Prepare table data
    const tableData = filteredMeasurements.map((m, index) => {
        const prev = filteredMeasurements[index + 1];
        const calculateDelta = (current?: number, previous?: number) => {
            if (current === undefined || previous === undefined) return '-';
            const diff = current - previous;
            return diff > 0 ? `+${diff.toFixed(1)}` : diff.toFixed(1);
        };

        const calculateBMI = (weight?: number, height?: number) => {
            if (!weight || !height || height === 0) return '-';
            const heightInMeters = height / 100;
            return (weight / (heightInMeters * heightInMeters)).toFixed(1);
        };

        return [
            new Date(m.timestamp).toLocaleDateString('pt-BR'),
            m.weight ? `${m.weight.toFixed(1)} kg\n${calculateDelta(m.weight, prev?.weight)}` : '-',
            m.height ? `${m.height.toFixed(1)} cm` : '-',
            calculateBMI(m.weight, m.height),
            m.bodyFatPercentage ? `${m.bodyFatPercentage.toFixed(1)}%\n${calculateDelta(m.bodyFatPercentage, prev?.bodyFatPercentage)}` : '-',
            m.muscleMass ? `${m.muscleMass.toFixed(1)} kg\n${calculateDelta(m.muscleMass, prev?.muscleMass)}` : '-',
            m.waistCircumference ? `${m.waistCircumference.toFixed(1)} cm` : '-',
            m.hipCircumference ? `${m.hipCircumference.toFixed(1)} cm` : '-',
        ];
    });

    // Create table
    autoTable(doc, {
        startY: 50,
        head: [['Data', 'Peso', 'Altura', 'IMC', '% Gordura', 'Massa Muscular', 'Cintura', 'Quadril']],
        body: tableData,
        theme: 'grid',
        styles: {
            fontSize: 8,
            cellPadding: 3,
        },
        headStyles: {
            fillColor: [14, 165, 233], // #0ea5e9
            textColor: [255, 255, 255],
            fontStyle: 'bold',
        },
        alternateRowStyles: {
            fillColor: [248, 250, 252], // #f8fafc
        },
    });

    // Summary statistics
    const finalY = (doc as any).lastAutoTable.finalY || 50;

    if (filteredMeasurements.length >= 2) {
        const latest = filteredMeasurements[0];
        const oldest = filteredMeasurements[filteredMeasurements.length - 1];

        doc.setFontSize(12);
        doc.setTextColor(15, 23, 42);
        doc.text('Resumo de Evolução', 14, finalY + 15);

        doc.setFontSize(9);
        doc.setTextColor(71, 85, 105);

        let summaryY = finalY + 22;

        if (latest.weight && oldest.weight) {
            const weightDiff = latest.weight - oldest.weight;
            doc.text(
                `Peso: ${weightDiff > 0 ? '+' : ''}${weightDiff.toFixed(1)} kg (${oldest.weight.toFixed(1)} → ${latest.weight.toFixed(1)} kg)`,
                14,
                summaryY
            );
            summaryY += 6;
        }

        if (latest.bodyFatPercentage && oldest.bodyFatPercentage) {
            const fatDiff = latest.bodyFatPercentage - oldest.bodyFatPercentage;
            doc.text(
                `Gordura Corporal: ${fatDiff > 0 ? '+' : ''}${fatDiff.toFixed(1)}% (${oldest.bodyFatPercentage.toFixed(1)}% → ${latest.bodyFatPercentage.toFixed(1)}%)`,
                14,
                summaryY
            );
            summaryY += 6;
        }

        if (latest.muscleMass && oldest.muscleMass) {
            const muscleDiff = latest.muscleMass - oldest.muscleMass;
            doc.text(
                `Massa Muscular: ${muscleDiff > 0 ? '+' : ''}${muscleDiff.toFixed(1)} kg (${oldest.muscleMass.toFixed(1)} → ${latest.muscleMass.toFixed(1)} kg)`,
                14,
                summaryY
            );
        }
    }

    // Footer
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(148, 163, 184); // #94a3b8
        doc.text(
            `Página ${i} de ${pageCount} - Gerado via NutriSync Web`,
            doc.internal.pageSize.getWidth() / 2,
            doc.internal.pageSize.getHeight() - 10,
            { align: 'center' }
        );
    }

    // Generate filename
    const filename = `medicoes_${patientName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;

    // Save PDF
    doc.save(filename);
};
